import pluralize from 'pluralize';
import { createTranslationKey } from './createTranslationKey';

// Everything below is written into the document and parsed back by the
// generator: styles, attributes and display texts must stay exactly as they
// are.
export const CONDITION_SPAN_STYLE =
  'background-color: #ffe5e5; color:rgba(207, 141, 19, 0.84); padding: 2px 4px; border-radius: 4px; font-weight: 600;';

export const KEY_SPAN_STYLE =
  'background-color: #f0f0f0; padding: 2px 4px; border-radius: 4px;';

const CONDITIONAL_KEYS = [
  'IF',
  'ELSE',
  'END-IF',
  '==',
  '!=',
  '<',
  '<=',
  '>',
  '>='
];

// The opener and the operators are typed into: the opener receives its key,
// an operator its value. The end markers have nothing to type inside; left
// editable, they swallowed the caret placed right before them, so a space
// typed between a key and {{Fin Si}} landed inside the marker.
const EDITABLE_CONDITION_KEYS = ['IF', '==', '!=', '<', '<=', '>', '>='];

/**
 * The condition markers the panel offers. `displayText` is the visible text
 * of the inserted span.
 */
export const CONDITION_BUTTONS = [
  { id: 'if', value: '{{IF}}', displayText: '{{Si ...}}' },
  { id: 'else', value: '{{ELSE}}', displayText: '{{Sinon}}' },
  { id: 'end_if', value: '{{END-IF}}', displayText: '{{Fin Si}}' }
];

/**
 * The comparison operators. Labels are persisted in `data-condition-label`
 * and in the visible text, hence not translated.
 */
export const OPERATORS = [
  { label: 'EST EGALE A', symbol: '==' },
  { label: 'DIFFERENT DE', symbol: '!=' },
  { label: 'INFERIEUR A', symbol: '<' },
  { label: 'INFERIEUR OU EGALE A', symbol: '<=' },
  { label: 'SUPERIEUR A', symbol: '>' },
  { label: 'SUPERIEUR OU EGALE A', symbol: '>=' }
];

/**
 * Inserts a template key into the editor at the caret.
 * @function
 *
 * @param {Object} editor - The Jodit editor instance.
 * @param {string} value - The technical key, braces included.
 * @param {string} type - The type of the selected item.
 * @param {Function} t - Translation function for localizing the option labels.
 * @param {string} [selectedLabel] - Human-readable label selected in the panel.
 * @returns {void}
 */
export const insertTemplateKey = (editor, value, type, t, selectedLabel) => {
  if (editor) {
    if (type === 'Photo') {
      editor.s.insertHTML(`
        <table style="border-collapse:collapse;width: 100%;">
          <tbody>
            <tr>
                <td style="width: 100%;">${value}<br></td>
            </tr>
          </tbody>
        </table>
      `);
    } else {
      const technicalKey = value;
      const rawKey = value.replace(/{{|}}/g, '');
      const isConditionalKey = CONDITIONAL_KEYS.includes(rawKey);
      const displayText =
        selectedLabel ||
        (rawKey.includes('template-document')
          ? createTranslationKey('tag', rawKey, t, pluralize)
          : rawKey
              .split('.')
              .map((part) => t(`template-documents.display_text.${part}`))
              .join('.'));
      const spanStyle = isConditionalKey
        ? CONDITION_SPAN_STYLE
        : KEY_SPAN_STYLE;
      const isEditable = EDITABLE_CONDITION_KEYS.includes(rawKey)
        ? 'true'
        : 'false';
      editor.s.insertHTML(
        `<span data-template-key="${technicalKey}" contenteditable="${isEditable}" style="${spanStyle}">${displayText}</span>`
      );
    }
    editor.selection.focus(); // Focus back to the editor
  }
};

/**
 * Bookmarks the caret before the focus leaves the editor for a panel input.
 * Jodit drops two hidden markers on the selection and restores them itself
 * inside `insertHTML`; `getEditorValue` strips them from the saved value.
 * @function
 * @param {Object} editor - The Jodit editor instance.
 * @returns {void}
 */
export const saveEditorSelection = (editor) => {
  if (!editor?.s?.isFocused?.()) return;

  editor.s.removeMarkers();
  editor.s.save();
};

/**
 * Brings the caret back inside the editor before its selection is read.
 * @function
 * @param {Object} editor - The Jodit editor instance.
 * @returns {void}
 */
export const restoreEditorSelection = (editor) => {
  if (editor?.s?.isFocused?.() !== false) return;

  editor.s.focus?.();
  editor.s.restore?.();
};

/**
 * Gets the previous template key from the current selection.
 * @function
 * @param {Object} editor - The Jodit editor instance.
 * @returns {string} The previous template key.
 */
export const getPreviousTemplateKey = (editor) => {
  const selection = globalThis.getSelection?.();
  if (!selection?.rangeCount) return '';

  let node = selection.getRangeAt(0).startContainer;
  if (node?.nodeType === 3) node = node.parentElement;
  if (!node) return '';

  const editorRoot = editor?.editor;

  while (node && node !== editorRoot) {
    let previous = node.previousSibling;

    while (previous) {
      if (
        previous.nodeType === 1 &&
        previous.getAttribute('data-template-key')
      ) {
        return previous.getAttribute('data-template-key');
      }

      if (previous.nodeType === 1) {
        const nestedTemplateKey = previous.querySelector('[data-template-key]');
        if (nestedTemplateKey) {
          return nestedTemplateKey.getAttribute('data-template-key');
        }
      }
      previous = previous.previousSibling;
    }
    node = node.parentElement;
  }

  return '';
};

/**
 * Inserts an operator condition bound to the key preceding the caret.
 * @function
 * @param {Object} editor - The Jodit editor instance.
 * @param {string} operatorSymbol - The symbol of the operator.
 * @param {string} operatorLabel - The label of the operator.
 * @returns {void}
 */
export const insertOperatorCondition = (
  editor,
  operatorSymbol,
  operatorLabel
) => {
  if (!editor) return;

  // The caret may sit in the panel search box; the left operand is read from
  // the editor selection, so it has to come back first.
  restoreEditorSelection(editor);
  const leftOperand = getPreviousTemplateKey(editor);
  const technicalKey = `{{${operatorSymbol}}}`;
  const displayText = `{{${operatorLabel} }}`;
  const dataCondition = [leftOperand, operatorSymbol].join(' ').trim();

  editor.s.insertHTML(
    `<span data-template-key="${technicalKey}" data-condition="${dataCondition}" data-condition-operator="${operatorSymbol}" data-condition-label="${operatorLabel}" data-left-operand="${leftOperand}" contenteditable="true" style="${CONDITION_SPAN_STYLE}">${displayText}</span>`
  );
  editor.selection.focus();
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Updates the operator condition span.
 * @function
 * @param {Object} span - The span element to update.
 * @param {boolean} normalizeDisplay - Whether to normalize the display.
 * @returns {void}
 */
export const updateOperatorConditionSpan = (span, normalizeDisplay = false) => {
  if (!span) return;
  const conditionElement = span;

  const operator =
    conditionElement.getAttribute('data-condition-operator') || '';
  const label = conditionElement.getAttribute('data-condition-label') || '';
  const leftOperand = conditionElement.getAttribute('data-left-operand') || '';
  const text = (conditionElement.textContent || '')
    .replace(/\u00a0/g, ' ')
    .trim();

  const labelPattern = label
    .trim()
    .split(/\s+/)
    .map((part) => escapeRegExp(part))
    .join('\\s+');

  const labelRegex = new RegExp(
    `^\\{\\{\\s*${labelPattern}(?:\\s+(.*?))?\\s*\\}\\}$`
  );
  const match = text.match(labelRegex);
  const value = match?.[1]?.trim() || '';

  const normalizedDisplay = value ? `{{${label} ${value}}}` : `{{${label} }}`;
  const technicalKey = value ? `{{${operator} ${value}}}` : `{{${operator}}}`;
  const dataCondition = value
    ? [leftOperand, operator, value].join(' ').trim()
    : [leftOperand, operator].join(' ').trim();

  if (normalizeDisplay) {
    conditionElement.textContent = normalizedDisplay;
  }
  conditionElement.setAttribute('data-template-key', technicalKey);
  conditionElement.setAttribute('data-condition', dataCondition);
};

/**
 * Keeps operator condition spans in sync with what the author types in them.
 * Bound once per editor.
 * @function
 * @param {Object} editor - The Jodit editor instance.
 * @returns {void}
 */
export const bindOperatorConditionSync = (editor) => {
  const editorRoot = editor?.editor;
  if (!editorRoot || editorRoot.dataset.operatorConditionBound === 'true') {
    return;
  }

  const getCurrentConditionSpan = (target) => {
    if (target instanceof HTMLElement) {
      const fromTarget = target.closest('span[data-condition-operator]');
      if (fromTarget) return fromTarget;
    }

    const selection = globalThis.getSelection?.();
    if (!selection?.rangeCount) return null;

    let node = selection.anchorNode;
    if (!node) return null;
    if (node.nodeType === 3) node = node.parentElement;
    if (!(node instanceof HTMLElement)) return null;

    return node.closest('span[data-condition-operator]');
  };

  const inputSyncHandler = (event) => {
    const conditionSpan = getCurrentConditionSpan(event.target);
    if (!conditionSpan) return;
    updateOperatorConditionSpan(conditionSpan);
  };

  const blurSyncHandler = (event) => {
    const conditionSpan = getCurrentConditionSpan(event.target);
    if (!conditionSpan) return;
    updateOperatorConditionSpan(conditionSpan, true);
  };

  editorRoot.addEventListener('input', inputSyncHandler);
  editorRoot.addEventListener('blur', blurSyncHandler, true);
  editorRoot.dataset.operatorConditionBound = 'true';
};
